import React from 'react';
import { Box } from '@mui/material';

/** How long a field stays pointed at, in milliseconds. */
const SHOWN_FOR = 1500;

const PointedFieldsContext = React.createContext(null);

/**
 * Lets a field waiting on a sibling point at it.
 *
 * A field that cannot offer anything until another is filled in has no way to say so on its own: it
 * is disabled, so it draws nothing and explains nothing. Clicking it names the keys it is waiting
 * for here, and the fields under those keys mark themselves for as long as this holds them.
 *
 * Scoped to the surface rather than held globally, so two panels open at once cannot point at each
 * other's fields.
 *
 * @returns {React.JSX.Element}
 */
export const PointedFieldsProvider = function PointedFieldsProvider({
  children,
}) {
  const [pointed, setPointed] = React.useState([]);
  const clearing = React.useRef(null);

  React.useEffect(
    () => () => {
      if (clearing.current) clearTimeout(clearing.current);
    },
    []
  );

  const value = React.useMemo(
    () => ({
      pointed,
      pointAt: (keys) => {
        if (clearing.current) clearTimeout(clearing.current);

        setPointed(keys ?? []);
        clearing.current = setTimeout(() => setPointed([]), SHOWN_FOR);
      },
    }),
    [pointed]
  );

  return (
    <PointedFieldsContext.Provider value={value}>
      {children}
    </PointedFieldsContext.Provider>
  );
};

/**
 * Points at a list of keys, for a field to call when it is clicked while disabled.
 *
 * Reads as a no-op below a surface holding no provider, so a field works wherever it is drawn.
 *
 * @returns {Function}
 */
export const usePointAt = () => {
  const held = React.useContext(PointedFieldsContext);

  return held?.pointAt ?? (() => {});
};

/**
 * Whether a key is being pointed at.
 *
 * @param {String} key - The field's key.
 *
 * @returns {Boolean}
 */
export const useIsPointed = (key) => {
  const held = React.useContext(PointedFieldsContext);

  return Boolean(key) && Boolean(held?.pointed?.includes(key));
};

/**
 * Wraps a control that cannot be used yet, so clicking it points at the keys it is waiting for.
 *
 * A disabled control raises no events of its own; the click lands here instead. A control waiting
 * for nothing is passed through untouched, so this may wrap one whatever state it is in.
 *
 * @param {Object} props - Component props.
 * @param {Array} props.waitingOn - The keys to point at.
 *
 * @returns {React.JSX.Element}
 */
export const BlockedField = function BlockedField({ waitingOn, children }) {
  const pointAt = usePointAt();

  if (!waitingOn?.length) return children;

  // The controls it wraps are full-width inside their cell, so a full-width box around one leaves
  // the arrangement as it was while still standing where the click lands.
  return (
    <Box onClick={() => pointAt(waitingOn)} sx={{ width: '100%' }}>
      {children}
    </Box>
  );
};

export default PointedFieldsProvider;
